import PropTypes from "prop-types"
import { Tooltip, Dropdown } from "antd"
import { DSIcon } from "ui/Icon/DSIcon"
import { CHECK_IN_TOOLTIP, CHECK_IN_STATE } from "../checkInUtils"
import styles from "./CheckInBadge.module.css"

// Icon: check-out artwork once anyone is checked out (orange while partial, green when all
// out), otherwise the check-in artwork (grey pending / green active).
const iconFor = (state, checkedOutCount) => {
  if (state === CHECK_IN_STATE.OUT) return <DSIcon name="checkOut" variant="active" width={52} height={25} />
  if (checkedOutCount > 0) return <DSIcon name="checkOut" variant="partial" width={52} height={25} />
  if (state === CHECK_IN_STATE.PENDING) return <DSIcon name="checkIn" variant="pending" width={52} height={25} />
  return <DSIcon name="checkIn" variant="active" width={52} height={25} />
}

// Clickable check-in / check-out state cell shared by the reservation + scheduled surfaces.
//   pending → check in · in → check out · out → reset · partial → dropdown (check-in or check-out)
const CheckInBadge = ({ state, checkedInCount = 0, checkedOutCount = 0, totalGuests = 0, onCheckIn, onCheckOut, onReset }) => {
  const isPending = state === CHECK_IN_STATE.PENDING
  const showCount = state === CHECK_IN_STATE.PARTIAL
  const countText = checkedOutCount > 0 ? `${checkedOutCount}/${totalGuests}` : `${checkedInCount}/${totalGuests}`

  const content = (
    <>
      {iconFor(state, checkedOutCount)}
      {showCount && <span className={styles.count}>{countText}</span>}
    </>
  )

  // Partial → dropdown so the operator can check-in more or check-out those already in.
  if (state === CHECK_IN_STATE.PARTIAL) {
    return (
      <Dropdown
        trigger={["click"]}
        menu={{
          items: [
            { key: "in", label: "Check-in Guests" },
            { key: "out", label: "Check Out Guests" }
          ],
          onClick: ({ key }) => (key === "in" ? onCheckIn?.() : onCheckOut?.())
        }}
      >
        <button type="button" className={styles.badge}>
          {content}
        </button>
      </Dropdown>
    )
  }

  const handleClick = () => {
    if (state === CHECK_IN_STATE.PENDING) onCheckIn?.()
    else if (state === CHECK_IN_STATE.IN) onCheckOut?.()
    else if (state === CHECK_IN_STATE.OUT) onReset?.()
  }

  return (
    <Tooltip title={CHECK_IN_TOOLTIP[state]}>
      <button type="button" className={`${styles.badge} ${isPending ? styles.pending : ""}`} onClick={handleClick}>
        {content}
      </button>
    </Tooltip>
  )
}

CheckInBadge.propTypes = {
  state: PropTypes.oneOf(["pending", "partial", "in", "out"]).isRequired,
  checkedInCount: PropTypes.number,
  checkedOutCount: PropTypes.number,
  totalGuests: PropTypes.number,
  onCheckIn: PropTypes.func,
  onCheckOut: PropTypes.func,
  onReset: PropTypes.func
}

export default CheckInBadge
